import React, { FunctionComponent, memo } from "react";
import { Grid, Box, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useMobileLayout } from "@app/hooks";
import { paymentMethodDescriptions, PaymentMethodKey } from "@investorRequest/constants/paymentMethodDescriptions";

interface Props {
  paymentMethod: PaymentMethodKey;
  isInternalContext: boolean;
}

const useStyles = makeStyles((theme) => {
  const bodyBase = {
    color: theme.palette.grey[700],
    lineHeight: 1.55,
    letterSpacing: "0.2px",
  };

  return {
    container: {
      backgroundColor: theme.palette.grey[100],
      border: `1px solid ${theme.palette.grey[400]}`,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2.5, 3),
      marginTop: theme.spacing(-3),
      marginBottom: theme.spacing(-3),
      width: "100%",
    },
    heading: {
      fontFamily: theme.typography.fontFamily,
      fontWeight: 600,
      marginBottom: theme.spacing(1),
      color: theme.palette.text.primary,
    },
    body: bodyBase,
    internalNote: {
      ...bodyBase,
      marginTop: theme.spacing(0.75),
    },
  };
});

const PaymentMethodDescription: FunctionComponent<Props> = ({ paymentMethod, isInternalContext }) => {
  const classes = useStyles();
  const isMobile = useMobileLayout();
  const content = paymentMethodDescriptions[paymentMethod];

  if (!content) {
    return null;
  }

  const showInternalNote = paymentMethod === "fedwire" && isInternalContext && !!content.internalNote;

  return (
    <Grid item xs={12}>
      <Box
        data-testid="payment-method-description"
        role="region"
        aria-label={`${content.heading} description`}
        className={classes.container}
      >
        <Typography
          variant="bodySubhead2"
          component="h3"
          className={classes.heading}
          sx={{ letterSpacing: 0, fontSize: isMobile ? 18 : "inherit" }}
        >
          {content.heading}
        </Typography>
        <Typography variant="body2" className={classes.body}>
          {content.body}
        </Typography>
        {showInternalNote && (
          <Typography
            variant="body2"
            data-testid="payment-method-description-internal-note"
            className={classes.internalNote}
          >
            {content.internalNote}
          </Typography>
        )}
      </Box>
    </Grid>
  );
};

export default memo<Props>(PaymentMethodDescription);
